import { useEffect, useState } from "react";
import { SpiderwebLink } from "@/components/interlinking/SpiderwebLink";
import { RelatedInterlinks } from "@/components/interlinking/RelatedInterlinks";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { 
  MapPin, Mail, Phone, Award, Clock, Star, Trophy, Medal, Globe, Users, 
  Target, Dumbbell, GraduationCap, Image as ImageIcon, Video, Sparkles, 
  CheckCircle2, TrendingUp, Heart, Home, Building
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { TrainerOnboarding } from "@/components/trainers/TrainerOnboarding";
import { TrainerProfileCompletionBar } from "@/components/trainers/TrainerProfileCompletionBar";
import { TrainerValueActivator } from "@/components/trainers/TrainerValueActivator";
import { TrainerMonthlyMetrics } from "@/components/trainers/TrainerMonthlyMetrics";
import { UnifiedTrainerMetrics } from "@/components/trainers/UnifiedTrainerMetrics";
import { useABTest } from "@/hooks/useABTest";
import trainerCertifiedBadge from "@/assets/ekselencia-trainer-certified-badge.png";
import { generateTrainerMetaDescription } from "@/utils/metaDescriptions";
import { generateTrainerTitle } from "@/utils/seoTitles";
import { UnirseATribuButton } from "@/components/shared/UnirseATribuButton";
import Breadcrumbs from "@/components/Breadcrumbs";
import { SITE_URL } from "@/constants/seo";
import { buildPostalAddressStrict } from "@/utils/structuredData/postalAddress";
import { UniversalPublicBookingSection } from "@/components/booking/UniversalPublicBookingSection";
import { BookingActivationCard } from "@/components/booking/BookingActivationCard";
import { ProfessionalPromotionBanner } from "@/components/professionals/ProfessionalPromotionBanner";
import { ProfileManagementCard } from "@/components/professionals/ProfileManagementCard";
import { BannerVistaPropietario } from "@/components/professionals/BannerVistaPropietario";
import { useUniversalBookingAccess } from "@/hooks/useUniversalBookingAccess";
import { useUniversalLocations } from "@/hooks/useUniversalLocations";
import { PublicStatsDisplay } from "@/components/shared/PublicStatsDisplay";
const getMedalBySubscription = (subscriptionType: string) => {
  switch (subscriptionType) {
    case "lifetime":
      return { icon: Trophy, color: "text-yellow-500 bg-yellow-50 dark:bg-yellow-950", label: "Entrenador Elite" };
    case "yearly":
      return { icon: Medal, color: "text-blue-500 bg-blue-50 dark:bg-blue-950", label: "Entrenador Pro" };
    case "monthly":
      return { icon: Award, color: "text-green-500 bg-green-50 dark:bg-green-950", label: "Entrenador Verificado" };
    default:
      return { icon: Award, color: "text-gray-500 bg-gray-50 dark:bg-gray-950", label: "Entrenador" };
  }
};

export default function TrainerDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [trainer, setTrainer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [bookingRefreshKey, setBookingRefreshKey] = useState(0);
  
  // Check booking access for this trainer
  const bookingAccess = useUniversalBookingAccess('trainer', trainer?.id);
  
  // Get trainer locations
  const { activeLocations } = useUniversalLocations('trainer', trainer?.id);
  
  const { variant, trackConversion, isTestActive } = useABTest('trainer', trainer?.id || '');

  const displayData = {
    name: variant?.overrides?.title || trainer?.full_name,
    bio: variant?.overrides?.description || trainer?.bio,
    cta_text: variant?.overrides?.cta_text || 'Contactar Ahora'
  };

  useEffect(() => {
    loadTrainer();
  }, [slug]);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
      
      if (user && trainer && user.id === trainer.user_id) {
        const dismissedKey = `trainer-onboarding-dismissed-${trainer.id}`;
        const isDismissed = localStorage.getItem(dismissedKey);
        setShowOnboarding(!isDismissed);
      }
    };
    
    if (trainer) {
      checkUser();
    }
  }, [trainer]);

  const loadTrainer = async () => {
    try {
      const { data, error } = await supabase
        .from("trainer_profiles_public")
        .select("*")
        .eq("slug", slug as string)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        navigate("/trainers");
        return;
      }

      setTrainer(data);
    } catch (error) {
      console.error("Error loading trainer:", error);
      navigate("/trainers");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-grow pt-32 pb-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <Breadcrumbs
              items={[
                { name: "Inicio", path: "/" },
                { name: "Entrenadores", path: "/trainers" },
                { name: trainer?.full_name || "Perfil", path: "" },
              ]}
            />
            <Skeleton className="h-96 mb-8" />
            <Skeleton className="h-64" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const medal = getMedalBySubscription(trainer.subscription_type);
  const MedalIcon = medal.icon;

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Person",
      name: trainer.full_name,
      description: trainer.bio,
      image: trainer.profile_image,
      jobTitle: "Entrenador Personal Certificado",
      ...(buildPostalAddressStrict({
        streetAddress: trainer.location_address,
        addressLocality: trainer.location_city,
        postalCode: trainer.location_postal_code,
        addressRegion: trainer.location_region,
        addressCountry: trainer.location_country || "ES",
      })
        ? {
            address: buildPostalAddressStrict({
              streetAddress: trainer.location_address,
              addressLocality: trainer.location_city,
              postalCode: trainer.location_postal_code,
              addressRegion: trainer.location_region,
              addressCountry: trainer.location_country || "ES",
            }),
          }
        : {}),
      email: trainer.email,
      telephone: trainer.phone,
      knowsAbout: trainer.specialty,
      award: trainer.achievements,
      hasCredential: trainer.certifications,
      makesOffer: {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Entrenamiento Personal",
          description: "Sesiones personalizadas de entrenamiento físico",
        },
        price: trainer.hourly_rate,
        priceCurrency: "EUR",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Inicio",
          item: SITE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Entrenadores",
          item: `${SITE_URL}/trainers`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: trainer.specialty || "Entrenador Personal",
          item: `${SITE_URL}/trainers?specialty=${trainer.specialty || 'entrenador-personal'}`,
        },
        {
          "@type": "ListItem",
          position: 4,
          name: trainer.full_name,
        },
      ],
    },
  ];

  const metaDescription = generateTrainerMetaDescription({
    full_name: trainer.full_name,
    location_city: trainer.location_city,
    bio: trainer.bio,
    specialty: trainer.specialty,
    years_experience: trainer.years_experience,
    hourly_rate: trainer.hourly_rate,
    clients_trained: trainer.clients_trained,
    certifications: trainer.certifications
  });

  const benefits = [
    { icon: Target, title: "Entrenamiento Personalizado", desc: "Planes adaptados a tus objetivos" },
    { icon: TrendingUp, title: "Resultados Garantizados", desc: "Progreso medible y efectivo" },
    { icon: Heart, title: "Seguimiento Continuo", desc: "Apoyo constante en tu progreso" },
    { icon: Users, title: "Experiencia Comprobada", desc: `${trainer.clients_trained || 0}+ clientes entrenados` },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-background to-muted/20">
      <SEO
        title={generateTrainerTitle({
          name: trainer.full_name,
          city: trainer.location_city || 'España',
          specialty: trainer.specialty?.[0],
          yearsExperience: trainer.years_experience
        })}
        description={metaDescription}
        keywords={[
          trainer.full_name,
          `entrenador personal ${trainer.location_city}`,
          trainer.location_city,
          trainer.location_country,
          ...trainer.specialty || [],
          "personal trainer",
          "coach deportivo",
          "entrenamiento personalizado",
          "fitness coach"
        ]}
        ogImage={trainer.profile_image || '/default-listing-fallback.png'}
        ogType="profile"
        structuredData={structuredData}
      />
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <Navigation />

      {/* Owner View Banner - Only show if current user is the owner */}
      {currentUserId && trainer.user_id && currentUserId === trainer.user_id && (
        <BannerVistaPropietario
          profileType="trainer"
          profileId={trainer.id}
          profileSlug={trainer.slug}
        />
      )}

      <main className="flex-grow pt-32 pb-16">
        {/* Breadcrumb Navigation */}
        <div className="bg-muted/30 border-b">
          <div className="container mx-auto px-4 py-3">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">
                      <Home className="h-4 w-4" />
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/trainers">Entrenadores</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                {trainer.specialty && trainer.specialty.length > 0 && (
                  <>
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link to={`/trainers?specialty=${trainer.specialty[0]}`}>
                          {trainer.specialty[0]}
                        </Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                  </>
                )}
                <BreadcrumbItem>
                  <BreadcrumbPage className="max-w-[150px] truncate sm:max-w-none">
                    {trainer.full_name}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>

        <article className="container mx-auto px-4 max-w-7xl">
          {/* Hero Section - Premium Design */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary/90 to-secondary p-1 mb-12 shadow-2xl">
            <div className="bg-background rounded-[22px] overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Left: Profile Info */}
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full w-fit mb-6 animate-fade-in">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-semibold">Entrenador Personal Certificado</span>
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                    {displayData.name}
                  </h1>
                  
                  <div className="flex items-center gap-2 text-muted-foreground mb-4">
                    <MapPin className="w-5 h-5 text-primary" />
                    <span className="text-lg">{trainer.location_city}, {trainer.location_country}</span>
                  </div>

                  {/* Public Stats */}
                  <div className="mb-6">
                    <PublicStatsDisplay 
                      views={trainer.profile_views || 0} 
                      contactClicks={trainer.contact_clicks || 0}
                      variant="full"
                    />
                  </div>

                  {trainer.specialty && trainer.specialty.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-8">
                      {trainer.specialty.map((spec: string, index: number) => (
                        <Badge 
                          key={index} 
                          variant="secondary"
                          className="px-4 py-1.5 text-sm cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                          onClick={() => navigate(`/trainers?specialty=${encodeURIComponent(spec)}`)}
                        >
                          {spec}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      size="lg" 
                      className="bg-gradient-primary hover:opacity-90 transition-opacity text-lg px-8 py-6 shadow-lg hover-scale"
                      onClick={() => {
                        const element = document.getElementById('contact');
                        element?.scrollIntoView({ behavior: 'smooth' });
                        if (trackConversion) trackConversion('contact_click');
                      }}
                    >
                      <Trophy className="w-5 h-5 mr-2" />
                      {displayData.cta_text}
                    </Button>
                    <UnirseATribuButton
                      deporte={trainer.specialty?.[0]}
                      ciudad={trainer.location_city}
                      size="lg"
                      variant="outline"
                      className="text-lg px-8 py-6 hover-scale"
                    />
                  </div>
                </div>

                {/* Right: Profile Image */}
                <div className="relative h-96 md:h-auto">
                  {trainer.profile_image ? (
                    <img
                      src={trainer.profile_image}
                      alt={`${trainer.full_name} - Entrenador Personal Certificado en ${trainer.location_city}`}
                      className="w-full h-full object-cover"
                      loading="eager"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                      <Award className="w-32 h-32 text-primary/40" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent md:hidden" />
                  
                  {/* Badge overlay */}
                  {trainer.subscription_type && trainer.subscription_type !== 'free' && (
                    <div className="absolute top-4 right-4">
                      <img 
                        src={trainerCertifiedBadge} 
                        alt="Entrenador Certificado por Ekselencia"
                        className="w-24 h-24 object-contain animate-pulse drop-shadow-2xl"
                        title="Entrenador Certificado por Ekselencia.com"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Stats Cards */}
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 hover-scale">
                  <CardContent className="p-6 text-center">
                    <Users className="w-10 h-10 mx-auto mb-3 text-primary" />
                    <div className="text-3xl font-bold mb-1">{trainer.clients_trained || 0}+</div>
                    <div className="text-sm text-muted-foreground">Clientes Entrenados</div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-secondary/5 to-secondary/10 border-secondary/20 hover-scale">
                  <CardContent className="p-6 text-center">
                    <Clock className="w-10 h-10 mx-auto mb-3 text-secondary" />
                    <div className="text-3xl font-bold mb-1">{trainer.years_experience}</div>
                    <div className="text-sm text-muted-foreground">Años de Experiencia</div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-accent/5 to-accent/10 border-accent/20 hover-scale">
                  <CardContent className="p-6 text-center">
                    <Star className="w-10 h-10 mx-auto mb-3 text-accent" />
                    <div className="text-3xl font-bold mb-1">{trainer.hourly_rate}€</div>
                    <div className="text-sm text-muted-foreground">Por Hora</div>
                  </CardContent>
                </Card>
              </div>

              {/* Benefits Section */}
              <Card className="border-2 border-primary/20 overflow-hidden">
                <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 border-b">
                  <h2 className="text-2xl font-bold flex items-center gap-3">
                    <Trophy className="w-6 h-6 text-primary" />
                    ¿Por Qué Entrenar Conmigo?
                  </h2>
                </div>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex gap-4 items-start">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <benefit.icon className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold mb-1">{benefit.title}</h3>
                          <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Training Locations */}
              {activeLocations.length > 0 && (
                <Card>
                  <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
                    <h2 className="text-2xl font-bold flex items-center gap-3">
                      <Building className="w-6 h-6 text-primary" />
                      Dónde Entreno
                    </h2>
                  </div>
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      {activeLocations.map((location) => (
                        <div 
                          key={location.id} 
                          className="flex items-start gap-4 p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                        >
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{location.name}</p>
                              {location.is_primary && (
                                <Badge variant="secondary" className="text-xs">Principal</Badge>
                              )}
                            </div>
                            {(location.address || location.city) && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {[location.address, location.city].filter(Boolean).join(", ")}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Bio */}
              <Card>
                <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
                  <h2 className="text-2xl font-bold flex items-center gap-3">
                    <Target className="w-6 h-6 text-primary" />
                    Sobre Mí
                  </h2>
                </div>
                <CardContent className="p-6">
                  <p className="text-muted-foreground leading-relaxed text-lg"><SpiderwebLink text={displayData.bio || ''} /></p>
                </CardContent>
              </Card>

              {/* Related Interlinks */}
              <RelatedInterlinks 
                text={[displayData.bio, trainer.teaching_philosophy, trainer.training_approach].filter(Boolean).join(' ')} 
                maxLinks={4} 
              />

              {/* Teaching Philosophy */}
              {trainer.teaching_philosophy && (
                <Card>
                  <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
                    <h2 className="text-2xl font-bold flex items-center gap-3">
                      <GraduationCap className="w-6 h-6 text-primary" />
                      Filosofía de Entrenamiento
                    </h2>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-muted-foreground leading-relaxed">{trainer.teaching_philosophy}</p>
                  </CardContent>
                </Card>
              )}

              {/* Training Approach */}
              {trainer.training_approach && (
                <Card>
                  <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
                    <h2 className="text-2xl font-bold flex items-center gap-3">
                      <Dumbbell className="w-6 h-6 text-primary" />
                      Metodología de Entrenamiento
                    </h2>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-muted-foreground leading-relaxed">{trainer.training_approach}</p>
                  </CardContent>
                </Card>
              )}

              {/* Achievements */}
              {trainer.achievements && trainer.achievements.length > 0 && (
                <Card>
                  <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
                    <h2 className="text-2xl font-bold flex items-center gap-3">
                      <Award className="w-6 h-6 text-primary" />
                      Logros y Certificaciones
                    </h2>
                  </div>
                  <CardContent className="p-6">
                    <ul className="space-y-3">
                      {trainer.achievements.map((achievement: string, index: number) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{achievement}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Certifications */}
              {trainer.certifications && trainer.certifications.length > 0 && (
                <Card>
                  <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
                    <h2 className="text-2xl font-bold">Certificaciones Adicionales</h2>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex flex-wrap gap-2">
                      {trainer.certifications.map((cert: string, index: number) => (
                        <Badge key={index} variant="outline" className="px-4 py-2 text-sm">
                          <Award className="w-4 h-4 mr-2" />
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Gallery */}
              {trainer.gallery_images && trainer.gallery_images.length > 0 && (
                <Card>
                  <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
                    <h2 className="text-2xl font-bold flex items-center gap-3">
                      <ImageIcon className="w-6 h-6 text-primary" />
                      Galería
                    </h2>
                  </div>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {trainer.gallery_images.map((image: string, index: number) => (
                        <div key={index} className="aspect-square rounded-xl overflow-hidden">
                          <img 
                            src={image} 
                            alt={`Galería de ${trainer.full_name}, imagen ${index + 1}`}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                            width="400"
                            height="400"
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Video */}
              {trainer.video_intro_url && (
                <Card>
                  <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
                    <h2 className="text-2xl font-bold flex items-center gap-3">
                      <Video className="w-6 h-6 text-primary" />
                      Video de Presentación
                    </h2>
                  </div>
                  <CardContent className="p-6">
                    <div className="aspect-video rounded-xl overflow-hidden">
                      <iframe
                        src={trainer.video_intro_url.replace('watch?v=', 'embed/')}
                        title={`Video de presentación de ${trainer.full_name}`}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Booking Section - Public facing */}
              <UniversalPublicBookingSection
                key={bookingRefreshKey}
                providerType="trainer"
                providerId={trainer.id}
                providerName={trainer.full_name}
              />
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Profile Management Card - Top Priority for Owners */}
              <ProfileManagementCard
                profileId={trainer.id}
                profileSlug={trainer.slug}
                isOwner={currentUserId === trainer.user_id}
                isPremium={Boolean(trainer.subscription_type && trainer.subscription_type !== 'free')}
                professionalType="trainer"
                hasBookingAccess={bookingAccess.hasAccess}
                daysRemaining={bookingAccess.daysRemaining}
                subscriptionTier={bookingAccess.subscriptionTier}
                onBookingActivated={() => setBookingRefreshKey(prev => prev + 1)}
              />

              {/* Contact Card */}
              <Card id="contact" className="sticky top-24 border-2 border-primary/20 shadow-xl">
                <div className="bg-gradient-primary text-white p-6">
                  <h3 className="text-2xl font-bold mb-2">Contacta Conmigo</h3>
                  <p className="opacity-90">¡Comienza tu transformación hoy!</p>
                </div>
                <CardContent className="p-6 space-y-4">
                  {trainer.email && (
                    <Button 
                      className="w-full justify-start text-left h-auto py-4 hover-scale" 
                      variant="outline"
                      asChild
                    >
                      <a href={`mailto:${trainer.email}`}>
                        <Mail className="w-5 h-5 mr-3 text-primary" />
                        <div>
                          <div className="text-xs text-muted-foreground">Email</div>
                          <div className="font-semibold">{trainer.email}</div>
                        </div>
                      </a>
                    </Button>
                  )}
                  {trainer.phone && (
                    <Button 
                      className="w-full justify-start text-left h-auto py-4 hover-scale" 
                      variant="outline"
                      asChild
                    >
                      <a href={`tel:${trainer.phone}`}>
                        <Phone className="w-5 h-5 mr-3 text-primary" />
                        <div>
                          <div className="text-xs text-muted-foreground">Teléfono</div>
                          <div className="font-semibold">{trainer.phone}</div>
                        </div>
                      </a>
                    </Button>
                  )}
                  {trainer.languages && trainer.languages.length > 0 && (
                    <div className="flex gap-3 p-4 bg-muted/50 rounded-lg">
                      <Globe className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Idiomas</div>
                        <div className="text-sm">{trainer.languages.join(", ")}</div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

{/* Owner-only components */}
{currentUserId === trainer.user_id && (
  <>
    <UnifiedTrainerMetrics 
      trainerId={trainer.id}
      trainerUserId={trainer.user_id}
      isPremium={Boolean(trainer.subscription_type && trainer.subscription_type !== 'free')}
      views={trainer.profile_views || 0}
    />
    <TrainerMonthlyMetrics 
      trainerId={trainer.id}
      subscriptionType={trainer.subscription_type}
    />

                  <TrainerValueActivator 
                    trainerId={trainer.id}
                    subscriptionType={trainer.subscription_type}
                    onDismiss={() => {}}
                  />

                  {showOnboarding && (
                    <TrainerOnboarding 
                      trainerId={trainer.id}
                      trainer={trainer}
                      onDismiss={() => {
                        localStorage.setItem(`trainer-onboarding-dismissed-${trainer.id}`, 'true');
                        setShowOnboarding(false);
                      }}
                    />
                  )}

                  <TrainerProfileCompletionBar 
                    trainer={trainer}
                    trainerId={trainer.id}
                  />
                </>
              )}

              {/* Booking Activation Request - for visitors */}
              {currentUserId !== trainer.user_id && (
                <BookingActivationCard listingId={trainer.id} listingName={trainer.full_name} />
              )}

              {/* Contextual Promotion Banner */}
              <ProfessionalPromotionBanner 
                type="trainer"
                isAuthenticated={!!currentUserId}
                hasProfile={currentUserId === trainer.user_id}
                compact={false}
              />
            </aside>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
